import {
  PartnerComparisonAdditionalInfoRow,
  PartnerComparisonAdditionalInfoTD,
  PartnerComparisonStyledTable,
  PartnerComparisonStyledText,
  PartnerComparisonTableInlineExpander,
} from './partner_comparison_table.styles'
import { Text } from '@amzn/storm-ui'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PartnerDetails } from '../../../directory/components/partner_comparison/compared_partners_context'
import { directoryStrings } from '../../../directory/directory_i18n_mappings'
import { CompanyListingServiceModels, Industries, LanguagesSupported } from '../../../../generated/graphql'
import { INDUSTRIES_TRANSLATION_TOKENS, LANGUAGES_TRANSLATION_TOKENS, SERVICE_MODELS_TRANSLATION_TOKENS } from '../../../../shared/shared_i18n_mappings'
import { formatBudgets, formatLocations } from '../../../directory/shared'
import { clickPartnerComparisonDetailsMetricHandler } from '../../../../util/partner_comparison_metrics_helper'

enum MoreDetailsGroup {
  Industries = 'Industries',
  ServiceModels = 'Service model',
  MinimumMonthlyBudget = 'Minimum monthly budget',
  Languages = 'Languages',
  OfficeLocations = 'Office locations',
}

export const MORE_DETAILS_SECTION_KEY_MAP: Map<string, string> = new Map([
  [MoreDetailsGroup.Industries, 'SHARED:INDUSTRIES'],
  [MoreDetailsGroup.ServiceModels, 'SHARED:SERVICE_MODEL'],
  [MoreDetailsGroup.MinimumMonthlyBudget, 'DETAILS_PAGE:INFO:MINIMUM_MONTHLY_BUDGET'],
  [MoreDetailsGroup.Languages, 'SHARED:LANGUAGES'],
  [MoreDetailsGroup.OfficeLocations, 'COMPARISON_DETAILS:TABLE:OFFICE_LOCATIONS'],
])

enum OfferFieldEnum {
  Industries = 'industries',
  ServiceModels = 'serviceModels',
  Languages = 'languages',
}

type OfferField = Pick<PartnerDetails, OfferFieldEnum>

interface MoreDetailsRowProps {
  selectedPartners: PartnerDetails[]
  groupName: MoreDetailsGroup
  offerField: OfferFieldEnum
  translationTokens: Map<string, string>
  emptyColumns: JSX.Element[] | React.ReactFragment
}

const MoreDetailsCompareRow: React.FunctionComponent<MoreDetailsRowProps> = (props: MoreDetailsRowProps): JSX.Element => {
  const { selectedPartners, groupName, offerField, translationTokens, emptyColumns } = props
  const { t } = useTranslation()

  return (
    <PartnerComparisonAdditionalInfoRow>
      <PartnerComparisonAdditionalInfoTD>
        <Text>{t(MORE_DETAILS_SECTION_KEY_MAP.get(groupName) ?? '')}</Text>
      </PartnerComparisonAdditionalInfoTD>
      {selectedPartners.map((partner: PartnerDetails, index: number) => {
        const offerValues: string = [...(partner[offerField as keyof OfferField] ?? [])]
          .map((offerKey: Industries | CompanyListingServiceModels | LanguagesSupported) => t(translationTokens.get(offerKey) ?? ''))
          .join(', ')

        return (
          <PartnerComparisonAdditionalInfoTD key={index}>
            <PartnerComparisonStyledText>{offerValues}</PartnerComparisonStyledText>
          </PartnerComparisonAdditionalInfoTD>
        )
      })}
      {emptyColumns}
    </PartnerComparisonAdditionalInfoRow>
  )
}

interface MoreDetailsComparisonTableProps {
  selectedPartners: PartnerDetails[]
  emptyColumnCount: number
}

export const MoreDetailsComparisonTable: React.FunctionComponent<MoreDetailsComparisonTableProps> = (props: MoreDetailsComparisonTableProps): JSX.Element => {
  const { selectedPartners, emptyColumnCount } = props
  const [isExpanderOpen, setIsExpanderOpen] = useState(true)
  const { t } = useTranslation()

  const emptyColumns: JSX.Element[] | React.ReactFragment =
    emptyColumnCount === 0 ? <></> : [...Array(emptyColumnCount)].map((_, i) => <PartnerComparisonAdditionalInfoTD key={i} />)

  const handleClickExpander = (): void => {
    clickPartnerComparisonDetailsMetricHandler('partnerComparison.page.moreDetails.expander.click')
    setIsExpanderOpen(prev => !prev)
  }

  const moreDetailsTable = useMemo(
    () => (
      <PartnerComparisonStyledTable>
        <tbody>
          <MoreDetailsCompareRow
            selectedPartners={selectedPartners}
            groupName={MoreDetailsGroup.Industries}
            offerField={OfferFieldEnum.Industries}
            translationTokens={INDUSTRIES_TRANSLATION_TOKENS}
            emptyColumns={emptyColumns}
          />
          <MoreDetailsCompareRow
            selectedPartners={selectedPartners}
            groupName={MoreDetailsGroup.ServiceModels}
            offerField={OfferFieldEnum.ServiceModels}
            translationTokens={SERVICE_MODELS_TRANSLATION_TOKENS}
            emptyColumns={emptyColumns}
          />
          <PartnerComparisonAdditionalInfoRow>
            <PartnerComparisonAdditionalInfoTD>
              <Text>{t(MORE_DETAILS_SECTION_KEY_MAP.get(MoreDetailsGroup.MinimumMonthlyBudget) ?? '')}</Text>
            </PartnerComparisonAdditionalInfoTD>
            {selectedPartners.map((partner: PartnerDetails, index: number) => {
              const budgets = formatBudgets(partner?.budgetRequirements ?? [], t)
              return (
                <PartnerComparisonAdditionalInfoTD key={index}>
                  <PartnerComparisonStyledText>{budgets}</PartnerComparisonStyledText>
                </PartnerComparisonAdditionalInfoTD>
              )
            })}
            {emptyColumns}
          </PartnerComparisonAdditionalInfoRow>
          <MoreDetailsCompareRow
            selectedPartners={selectedPartners}
            groupName={MoreDetailsGroup.Languages}
            offerField={OfferFieldEnum.Languages}
            translationTokens={LANGUAGES_TRANSLATION_TOKENS}
            emptyColumns={emptyColumns}
          />
          <PartnerComparisonAdditionalInfoRow>
            <PartnerComparisonAdditionalInfoTD>
              <Text>{t(MORE_DETAILS_SECTION_KEY_MAP.get(MoreDetailsGroup.OfficeLocations) ?? '')}</Text>
            </PartnerComparisonAdditionalInfoTD>
            {selectedPartners.map((partner: PartnerDetails, index: number) => {
              const locations = formatLocations(partner.regionalOffices ?? [], t)
              return (
                <PartnerComparisonAdditionalInfoTD key={index}>
                  <PartnerComparisonStyledText>{locations}</PartnerComparisonStyledText>
                </PartnerComparisonAdditionalInfoTD>
              )
            })}
            {emptyColumns}
          </PartnerComparisonAdditionalInfoRow>
        </tbody>
      </PartnerComparisonStyledTable>
    ),
    [selectedPartners, emptyColumns, t],
  )

  return (
    <PartnerComparisonTableInlineExpander
      label={
        <Text type={'h3'} textColor={'base'}>
          {t(directoryStrings.TABLE_MORE_DETAILS)}
        </Text>
      }
      isOpen={isExpanderOpen}
      handleClick={handleClickExpander}
    >
      {moreDetailsTable}
    </PartnerComparisonTableInlineExpander>
  )
}
